/* eslint-disable no-console */
import { Router } from '@lightningjs/sdk';
import { descriptors } from '@tornado-rdk/typography';
import Store from 'lightningjs-redux';
import _merge from 'lodash/merge';
import { cleanupIconsCache } from '../../../tornado/icon/icon.provider';
import { getFullPath } from '../../../settings/app.settings';
import routes from '../../../routes/routes';
import Sidenav from '../../../sidenav/sidenav.component';
import { animationConfig, WIDTH, EXPANDED_WIDTH } from '../../../sidenav/sidenav.constants';
import { defaultContext, getInitialStyle, colors } from './app.style';
import connect from './app.connector';
import PopupContainer from '../../../popupContainer/popupContainer.component';
import {
  EXIT_APP_SIGNAL,
  HIDE_POPUP_SIGNAL,
  ROUTE_BACK_SIGNAL,
} from '../../../popupContainer/popupContainer.constants';
import { showPopupAction } from '../../../../logic/popup/popup.actions';
import { EXIT_POPUP_ID } from '../../../popups/popups.constants';
import { CookieConsent } from '../../../cookieConsent/cookieConsent.component';
import ScreenSaver from '../../../screenSaver/screenSaver.component';
import {
  BANNED_POPUPS,
  BANNED_WIDGETS,
  HIDE_SCREENSAVER_SIGNAL,
  SHOW_SCREENSAVER_SIGNAL,
} from '../../../screenSaver/screenSaver.constants';
import { closeApp, patchKeyListener } from '../../utils/app.utils';
import { destroy as destroyI18NBundle } from '../../../i18n';
import { cleanupConfig, cleanupGlobals } from '../../../../logic/app/app.utils';
import { VIDEO_PATH, LIVE_PATH, HOME_PATH, DEEP_LINK_EXCLUSIONS } from '../../../routes/routes.utils';
import { setScreenSaverIsLocked } from '../../../../logic/screenSaver/screenSaver.actions';
import { cleanupEngines } from '../../../../logic/analytics/analytics.utils';
import { clearCache } from '../../../../logic/layout/layout.actions';
import { cleanupVirtualizationCache } from '../../../tornado/utils/virtualization';
import { unsubscribeThirdPartyErrorHandlers } from '../../../../thirdParty/newrelic/newrelic.errorHandler';
import { SnackBar } from '../../../tornado/snack-bar/snackBar.component';
import LifecycleService from '../../../../logic/lifecycle/platform/default';
import PerformanceMonitor from '../../../performanceMonitor/performanceMonitor.component';
import { isPerformanceMonitorEnabled } from '../../../performanceMonitor/performanceMonitor.constants';

export default class App extends Router.App {
  static getFonts() {
    // FIXME: Ideally, we should support "graceful degradation" approach here: load woff2 and use woff as fallback
    // See https://github.com/rdkcentral/Lightning-SDK/issues/185

    return descriptors.map(({ url, ...rest }) => ({
      url: getFullPath(url),
      ...rest,
    }));
  }

  static _template() {
    return _merge(
      {
        ...super._template(),
        forceZIndexContext: true,
        Pages: {
          zIndex: 0,
        },
        Widgets: {
          zIndex: 100,
          forceZIndexContext: true,
          Sidenav: {
            type: Sidenav,
            zIndex: 101,
          },
          Popup: {
            type: PopupContainer,
            zIndex: 103,
            signals: {
              [EXIT_APP_SIGNAL]: '_handleExitApp',
              [HIDE_POPUP_SIGNAL]: '_hidePopUp',
              [ROUTE_BACK_SIGNAL]: '_handleRouteBack',
            },
          },
          SnackBar: {
            type: SnackBar,
            zIndex: 104,
          },
          CookieConsent: {
            type: CookieConsent,
            zIndex: 105,
          },
          ScreenSaver: {
            type: ScreenSaver,
            zIndex: 106,
            signals: {
              [SHOW_SCREENSAVER_SIGNAL]: '_showScreensaver',
              [HIDE_SCREENSAVER_SIGNAL]: '_hideScreensaver',
            },
          },
        },
        ...(isPerformanceMonitorEnabled() ? { PerformanceMonitor: { type: PerformanceMonitor } } : {}),
      },
      getInitialStyle(),
    );
  }

  static _states() {
    const [Loading, WidgetsBaseState] = super._states();

    return [
      Loading,
      class Widgets extends WidgetsBaseState {
        static _states() {
          return [
            class CookieConsent extends this {
              set shouldDisplayCookieConsent(value) {
                Store.dispatch(setScreenSaverIsLocked(value));
                if (value) {
                  return;
                }

                // Cookie consent is hidden, checking if there is a pop-up on a background
                const popUpContainer = this.tag('Popup');
                if (popUpContainer.popupId) {
                  Router.focusWidget('Popup');
                  // NOTE: this is mandatory when switching state from one Widget to another
                  this.reload(popUpContainer);
                } else {
                  Router.restoreFocus();
                }
              }

              set popUp(value) {
                if (value) {
                  // NOTE: setting the pop-up content only, without focus switching
                  this.tag('Popup').content = value;
                }
              }

              _hidePopUp() {
                this.tag('Popup').reset();
              }

              _getFocused() {
                return this.tag('CookieConsent');
              }
            },
          ];
        }

        get currentWidgetRef() {
          return this._widget ? this._widget.ref : '';
        }

        /**
         * Switches from one widget to another while in the Widgets state
         * @override
         */
        reload(widget) {
          this._previousWidgetRef = this.currentWidgetRef;

          super.reload(widget);
        }

        _showScreensaver() {
          const { currentWidgetRef } = this;
          if (currentWidgetRef === 'Popup') {
            const popUpContainer = this.tag('Popup');
            if (BANNED_POPUPS.includes(popUpContainer.popupId)) {
              return;
            }
          }

          if (!BANNED_WIDGETS.includes(currentWidgetRef)) {
            super._showScreensaver();
          }
        }

        _hideScreensaver() {
          const previousWidget = this._previousWidgetRef ? this.tag(`Widgets.${this._previousWidgetRef}`) : null;
          if (previousWidget) {
            this.reload(previousWidget);
          } else {
            Router.restoreFocus();
          }
        }

        _handleRight() {
          Router.restoreFocus();
        }

        _handleKey() {
          return false;
        }

        $exit() {
          // One of the possible reasons to exit the Widgets state is the Sidenav closing
          this.tag('Pages').setSmooth('x', 0, animationConfig);
          // Since the focus restored on pages, resetting the screensaver as there is a possibility it was blocked by some widget
          this.tag('ScreenSaver').resetTimer();
          // This property is inherited from the Router's base app
          this._widget = null;
          this._previousWidgetRef = '';
        }
      },
    ];
  }

  _construct() {
    this._style = defaultContext;
  }

  _setup() {
    Router.startRouter(routes, this);
  }

  _attach() {
    this._unsubscribe = connect(this);
    this.tag('ScreenSaver').resetTimer();
    patchKeyListener(this);
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }

    // NOTE: it was needed for Metro single-page approach
    // this._cleanup();
  }

  set isAppInitialized(value) {
    this._isAppInitialized = value;
    if (value && !this._lifecyclesServise) {
      this._lifecyclesServise = new LifecycleService();
    }
  }

  set isOpenedViaDeeplink(value) {
    this._shouldNavigateToHomeOnBack = value;
  }

  set promptBeforeExitFeature(value) {
    this._promptBeforeExitFeature = value;
  }

  set shouldDisplayCookieConsent(value) {
    Store.dispatch(setScreenSaverIsLocked(value));

    if (value) {
      this._setState('Widgets.CookieConsent');
    }
  }

  set popUp(value) {
    if (value) {
      this.tag('Popup').content = value;
      Router.focusWidget('Popup');
    }
  }

  set currentRoute(value) {
    const livePath = `${LIVE_PATH}/`;
    const videoPath = `${VIDEO_PATH}/`;

    this._currentRoute = value;

    if (value.includes(livePath) || value.includes(videoPath)) {
      this.patch({
        color: colors.transparent,
      });
    }
  }

  _captureKey() {
    this.tag('ScreenSaver').resetTimer();

    return false;
  }

  _handleLeft() {
    const sidenav = this.tag('Sidenav');
    const isNavigationActive = sidenav.visible && sidenav.isActive;

    if (!isNavigationActive) {
      console.warn('[app] Navigation is not active');

      return false;
    }

    const activeWidget = Router.getActiveWidget();
    if (activeWidget && activeWidget.ref !== 'Sidenav') {
      return false;
    }

    this.tag('Pages').setSmooth(
      'x',
      EXPANDED_WIDTH[this._style.screenSize] - WIDTH[this._style.screenSize],
      animationConfig,
    );
    Router.focusWidget('Sidenav');

    return true;
  }

  // Should be triggered via fireAncestors by the children components
  $notification(notification) {
    this.tag('SnackBar').push(notification);
  }

  /**
   * Lighting SDK Router hook, will be executed when there is no routing history in stack
   */
  _handleAppClose() {
    if (this._shouldNavigateToHomeOnBack && !DEEP_LINK_EXCLUSIONS.includes(this._currentRoute)) {
      Router.navigate(HOME_PATH);

      return;
    }

    if (this._isAppInitialized && this._promptBeforeExitFeature) {
      Store.dispatch(showPopupAction(EXIT_POPUP_ID));
    } else {
      this._handleExitApp();
    }
  }

  _handleExitApp() {
    // NOTE: it was needed for Metro single-page approach
    // this._cleanup();
    closeApp(this.application);
  }

  _cleanup() {
    if (this._cleanupComplete) {
      return;
    }

    console.log('[app.component] Shutting down analytics engines');
    cleanupEngines();
    console.log('[app.component] Removing redundant window globals added during the app lifecycle');
    cleanupGlobals();
    console.log('[app.component] Purging views virtualization cache');
    cleanupVirtualizationCache();
    cleanupIconsCache();
    // TODO: cleanup other reducers if necessary
    console.log('[app.component] Purging layout API cache');
    Store.dispatch(clearCache());
    console.log('[app.component] Removing unsubscribed Redux observers');
    Store.cleanup();
    console.log('[app.component] Removing error handling observers');
    unsubscribeThirdPartyErrorHandlers();
    destroyI18NBundle();
    cleanupConfig();

    this._cleanupComplete = true;
  }

  _hidePopUp() {
    this.tag('Popup').reset();

    Router.restoreFocus();
  }

  _handleRouteBack() {
    Router.back();

    this._hidePopUp();
  }

  _showScreensaver() {
    if (this.tag('ScreenSaver').canBeShown) {
      Router.focusWidget('ScreenSaver');
    }
  }
}
